import { boolean, mixed, object, string } from 'yup';

import { formatDateForInput, formatForWire, hasNonNullValue, isNotDefined, parseNumbers } from 'utilities/common';

import { MAX_LENGTH_ERROR_MESSAGE, NULLABLE_BOOLEAN, NULLABLE_STRING, plusMinusFiveYearsDateSchema } from 'modules/common/constants';

export default class DestinationInformation {
  static schema = object().shape({
    isDestinationServiceInitiated: boolean(),
    isPurchasing: NULLABLE_BOOLEAN,
    purchasingDestinationHomeTypeId: NULLABLE_STRING,
    purchasingFamiliarWithAreaId: NULLABLE_STRING,
    purchasingMinPrice: string(),
    purchasingMaxPrice: string(),
    purchasingBedroomsPreferred: string(),
    purchasingBathroomsPreferred: string(),
    purchasingSquareFootage: string(),
    purchasingFireplacePreferenceId: NULLABLE_STRING,
    purchasingGaragePreferenceId: NULLABLE_STRING,
    purchasingBasementPreferenceId: NULLABLE_STRING,
    purchasingHasPreferredAgent: boolean(),
    purchasingPreferredAgentName: string().max(50, MAX_LENGTH_ERROR_MESSAGE),
    purchasingPreferredAgency: string().max(50, MAX_LENGTH_ERROR_MESSAGE),
    purchasingPreferredAgencyEmail: string().max(50, MAX_LENGTH_ERROR_MESSAGE),
    purchasingPreferredAgencyPhone: string().max(50, MAX_LENGTH_ERROR_MESSAGE),
    purchasingHasSchoolRequest: NULLABLE_BOOLEAN,
    purchasingSchoolRequest: string().max(5000, MAX_LENGTH_ERROR_MESSAGE),
    purchasingImportantFeaturesSpecialRequests: string().max(5000, MAX_LENGTH_ERROR_MESSAGE),

    isRenting: NULLABLE_BOOLEAN,
    rentingDateNeeded: plusMinusFiveYearsDateSchema,
    rentingDestinationHomeTypeId: NULLABLE_STRING,
    rentingFamiliarWithAreaId: NULLABLE_STRING,
    rentingMinPrice: string(),
    rentingMaxPrice: string(),
    rentingBedroomsPreferred: string(),
    rentingBathroomsPreferred: string(),
    rentingSquareFootage: string(),
    rentingFireplacePreferenceId: NULLABLE_STRING,
    rentingGaragePreferenceId: NULLABLE_STRING,
    rentingBasementPreferenceId: NULLABLE_STRING,
    rentingHasSchoolRequest: NULLABLE_BOOLEAN,
    rentingSchoolRequest: string().max(5000, MAX_LENGTH_ERROR_MESSAGE),
    rentingImportantFeaturesSpecialRequests: string().max(5000, MAX_LENGTH_ERROR_MESSAGE),
    rentingAreaTourAgentId: mixed().nullable(),
    rentingAreaTourInitiationDate: plusMinusFiveYearsDateSchema,
    rentingAreaTourCanceledDate: plusMinusFiveYearsDateSchema,
    rentingAreaTourCompletedDate: plusMinusFiveYearsDateSchema,
  });

  constructor(wireData) {
    const json = wireData || {};

    this.isDestinationServiceInitiated = !!json.isDestinationServiceInitiated;
    this.isPurchasing = json.isPurchasing;
    this.purchasingDestinationHomeTypeId = json.purchasingDestinationHomeTypeId;
    this.purchasingFamiliarWithAreaId = json.purchasingFamiliarWithAreaId;
    this.purchasingMinPrice = isNotDefined(json.purchasingMinPrice) ? '' : json.purchasingMinPrice;
    this.purchasingMaxPrice = isNotDefined(json.purchasingMaxPrice) ? '' : json.purchasingMaxPrice;
    this.purchasingBedroomsPreferred = isNotDefined(json.purchasingBedroomsPreferred) ? '' : json.purchasingBedroomsPreferred;
    this.purchasingBathroomsPreferred = isNotDefined(json.purchasingBathroomsPreferred) ? '' : json.purchasingBathroomsPreferred;
    this.purchasingSquareFootage = isNotDefined(json.purchasingSquareFootage) ? '' : json.purchasingSquareFootage;
    this.purchasingFireplacePreferenceId = json.purchasingFireplacePreferenceId;
    this.purchasingGaragePreferenceId = json.purchasingGaragePreferenceId;
    this.purchasingBasementPreferenceId = json.purchasingBasementPreferenceId;
    // Rendered by a checkbox, which needs a real boolean rather than the API's nullable one.
    this.purchasingHasPreferredAgent = !!json.purchasingHasPreferredAgent;
    this.purchasingPreferredAgentName = json.purchasingPreferredAgentName || '';
    this.purchasingPreferredAgency = json.purchasingPreferredAgency || '';
    this.purchasingPreferredAgencyEmail = json.purchasingPreferredAgencyEmail || '';
    this.purchasingPreferredAgencyPhone = json.purchasingPreferredAgencyPhone || '';
    this.purchasingHasSchoolRequest = json.purchasingHasSchoolRequest;
    this.purchasingSchoolRequest = json.purchasingSchoolRequest || '';
    this.purchasingImportantFeaturesSpecialRequests = json.purchasingImportantFeaturesSpecialRequests || '';

    this.isRenting = json.isRenting;
    this.rentingDateNeeded = formatDateForInput(json.rentingDateNeeded);
    this.rentingDestinationHomeTypeId = json.rentingDestinationHomeTypeId;
    this.rentingFamiliarWithAreaId = json.rentingFamiliarWithAreaId;
    this.rentingMinPrice = isNotDefined(json.rentingMinPrice) ? '' : json.rentingMinPrice;
    this.rentingMaxPrice = isNotDefined(json.rentingMaxPrice) ? '' : json.rentingMaxPrice;
    this.rentingBedroomsPreferred = isNotDefined(json.rentingBedroomsPreferred) ? '' : json.rentingBedroomsPreferred;
    this.rentingBathroomsPreferred = isNotDefined(json.rentingBathroomsPreferred) ? '' : json.rentingBathroomsPreferred;
    this.rentingSquareFootage = isNotDefined(json.rentingSquareFootage) ? '' : json.rentingSquareFootage;
    this.rentingFireplacePreferenceId = json.rentingFireplacePreferenceId;
    this.rentingGaragePreferenceId = json.rentingGaragePreferenceId;
    this.rentingBasementPreferenceId = json.rentingBasementPreferenceId;
    this.rentingHasSchoolRequest = json.rentingHasSchoolRequest;
    this.rentingSchoolRequest = json.rentingSchoolRequest || '';
    this.rentingImportantFeaturesSpecialRequests = json.rentingImportantFeaturesSpecialRequests || '';
    this.rentingAreaTourAgentId = isNotDefined(json.rentingAreaTourAgentId) ? '' : json.rentingAreaTourAgentId;
    this.rentingAreaTourInitiationDate = formatDateForInput(json.rentingAreaTourInitiationDate);
    this.rentingAreaTourCanceledDate = formatDateForInput(json.rentingAreaTourCanceledDate);
    this.rentingAreaTourCompletedDate = formatDateForInput(json.rentingAreaTourCompletedDate);
  }

  static fromWireData(wireData) {
    return new DestinationInformation(wireData);
  }

  // The Area Tour checkbox is not stored; it is derived from whether any area tour value is present.
  static hasAreaTourData(destination) {
    if (!destination) {
      return false;
    }

    return [
      destination.rentingAreaTourAgentId,
      destination.rentingAreaTourInitiationDate,
      destination.rentingAreaTourCanceledDate,
      destination.rentingAreaTourCompletedDate,
    ].some((value) => hasNonNullValue(value) && value !== '');
  }

  static generateErrors(errors) {
    return errors;
  }

  isPopulated() {
    return [
      this.isPurchasing,
      this.purchasingDestinationHomeTypeId,
      this.purchasingFamiliarWithAreaId,
      this.purchasingFireplacePreferenceId,
      this.purchasingGaragePreferenceId,
      this.purchasingBasementPreferenceId,
      this.isRenting,
      this.rentingDestinationHomeTypeId,
      this.rentingFamiliarWithAreaId,
      this.rentingFireplacePreferenceId,
      this.rentingGaragePreferenceId,
      this.rentingBasementPreferenceId,
    ].some(hasNonNullValue) || [
      this.purchasingMinPrice,
      this.purchasingMaxPrice,
      this.purchasingBedroomsPreferred,
      this.purchasingBathroomsPreferred,
      this.purchasingSquareFootage,
      this.purchasingHasPreferredAgent,
      this.purchasingPreferredAgentName,
      this.purchasingPreferredAgency,
      this.purchasingPreferredAgencyEmail,
      this.purchasingPreferredAgencyPhone,
      this.purchasingHasSchoolRequest,
      this.purchasingSchoolRequest,
      this.purchasingImportantFeaturesSpecialRequests,
      this.isRenting,
      this.rentingMinPrice,
      this.rentingMaxPrice,
      this.rentingBedroomsPreferred,
      this.rentingBathroomsPreferred,
      this.rentingSquareFootage,
      this.rentingHasSchoolRequest,
      this.rentingSchoolRequest,
      this.rentingImportantFeaturesSpecialRequests,
      this.rentingDateNeeded,
      this.rentingAreaTourAgentId,
      this.rentingAreaTourInitiationDate,
      this.rentingAreaTourCanceledDate,
      this.rentingAreaTourCompletedDate,
    ].some((value) => !!value);
  }

  toWireData() {
    return formatForWire({
      isDestinationServiceInitiated: this.isDestinationServiceInitiated,
      isPurchasing: this.isPurchasing,
      purchasingDestinationHomeTypeId: this.purchasingDestinationHomeTypeId,
      purchasingFamiliarWithAreaId: this.purchasingFamiliarWithAreaId,
      purchasingFireplacePreferenceId: this.purchasingFireplacePreferenceId,
      purchasingGaragePreferenceId: this.purchasingGaragePreferenceId,
      purchasingBasementPreferenceId: this.purchasingBasementPreferenceId,
      purchasingHasPreferredAgent: this.purchasingHasPreferredAgent,
      purchasingPreferredAgentName: this.purchasingPreferredAgentName,
      purchasingPreferredAgency: this.purchasingPreferredAgency,
      purchasingPreferredAgencyEmail: this.purchasingPreferredAgencyEmail,
      purchasingPreferredAgencyPhone: this.purchasingPreferredAgencyPhone,
      purchasingHasSchoolRequest: this.purchasingHasSchoolRequest,
      purchasingSchoolRequest: this.purchasingSchoolRequest,
      purchasingImportantFeaturesSpecialRequests: this.purchasingImportantFeaturesSpecialRequests,
      isRenting: this.isRenting,
      rentingDateNeeded: this.rentingDateNeeded,
      rentingDestinationHomeTypeId: this.rentingDestinationHomeTypeId,
      rentingFamiliarWithAreaId: this.rentingFamiliarWithAreaId,
      rentingFireplacePreferenceId: this.rentingFireplacePreferenceId,
      rentingGaragePreferenceId: this.rentingGaragePreferenceId,
      rentingBasementPreferenceId: this.rentingBasementPreferenceId,
      rentingHasSchoolRequest: this.rentingHasSchoolRequest,
      rentingSchoolRequest: this.rentingSchoolRequest,
      rentingImportantFeaturesSpecialRequests: this.rentingImportantFeaturesSpecialRequests,
      // Unchecking Area Tour on the form clears these; empty strings become null on the wire.
      rentingAreaTourAgentId: this.rentingAreaTourAgentId,
      rentingAreaTourInitiationDate: this.rentingAreaTourInitiationDate,
      rentingAreaTourCanceledDate: this.rentingAreaTourCanceledDate,
      rentingAreaTourCompletedDate: this.rentingAreaTourCompletedDate,

      ...parseNumbers({
        purchasingMinPrice: this.purchasingMinPrice,
        purchasingMaxPrice: this.purchasingMaxPrice,
        purchasingBedroomsPreferred: this.purchasingBedroomsPreferred,
        purchasingBathroomsPreferred: this.purchasingBathroomsPreferred,
        purchasingSquareFootage: this.purchasingSquareFootage,
        rentingMinPrice: this.rentingMinPrice,
        rentingMaxPrice: this.rentingMaxPrice,
        rentingBedroomsPreferred: this.rentingBedroomsPreferred,
        rentingBathroomsPreferred: this.rentingBathroomsPreferred,
        rentingSquareFootage: this.rentingSquareFootage,
      }),
    });
  }
}
